'use client';

import * as React from 'react';

import { cn } from '../../lib/cn';

export interface SegmentedTabItem<T extends string> {
	value: T;
	label: string;
}

export interface SegmentedTabsProps<T extends string> {
	items: readonly SegmentedTabItem<T>[];
	value: T;
	onChange: (value: T) => void;
	/** Etichetta del gruppo per gli screen reader. */
	ariaLabel?: string;
	className?: string;
}

/**
 * Selettore a pillole per stato **di pagina**, non di navigazione.
 *
 * Distinto da `NavPills`, che è fatto di link e cambia rotta: qui la scelta non
 * lascia la pagina — è la finestra temporale o la sezione che si sta guardando,
 * e deve restare un bottone perché non c'è un indirizzo da condividere.
 *
 * `role="group"` e `aria-pressed` invece di `radiogroup`: le frecce non devono
 * spostare la selezione, perché ogni cambio rilancia una fetch e attraversare
 * cinque periodi da tastiera ne lancerebbe cinque.
 */
export function SegmentedTabs<T extends string>({
	items,
	value,
	onChange,
	ariaLabel,
	className,
}: SegmentedTabsProps<T>) {
	return (
		<div role="group" aria-label={ariaLabel} className={cn('mb-4 flex flex-wrap gap-2', className)}>
			{items.map((item) => (
				<button
					key={item.value}
					type="button"
					onClick={() => onChange(item.value)}
					aria-pressed={value === item.value}
					className={cn('tap-target dash-tab', value === item.value && 'dash-tab-active')}
				>
					{item.label}
				</button>
			))}
		</div>
	);
}
SegmentedTabs.displayName = 'SegmentedTabs';
